import React, { useState, useEffect, FC, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import IconButton from "@material-ui/core/IconButton";
import { Error } from '@material-ui/icons';
import { 
    ArrowBack, 
    LocalAtm, 
    LocalActivityOutlined, 
    ExpandMore 
} from "@material-ui/icons";
import { 
    Button, 
    Container, 
    Grid, 
    Paper, 
    Typography,
    OutlinedInput,
    InputAdornment,
    InputLabel,
    FormControl,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import {
    MuiPickersUtilsProvider,
    KeyboardTimePicker,
    KeyboardDatePicker,
} from '@material-ui/pickers';

import { ModalLogout } from '../../components/Modal/ModalLogout';
import { ScheduleNoticeModal } from '../../components/Modal/ScheduleNoticeModal';
import ScreenLoader from "../../components/ScreenLoader";
import Alert from '../../components/Alert/AlertComponent';
import { createOrder } from '../../flux/actions/order-actions';
import ORDER_CONSTANTS from '../../flux/constants/order-constants';
import { removeFromCart, updateQty } from '../../flux/actions/shoppingCart-actions';
import { getAddressAction } from '../../flux/actions/deliveryAddress-actions';
import UserModel from '../../models/user-model';
import { dateToUnixTime } from '../../helpers/validations';
import { AppContext } from '../OrderFlux/Context';

import { useStyles } from './styles';
import varStyles from '../../styles/Vars';
import 'date-fns';
import SnackBarAlert from '../../components/SnackBar/SnackBarAlert';
import { CartItems } from './CartItems';
import { EmailTemplates } from '../../services/email/EmailTemplates';
import { sendOrderConfirmation } from '../../services/email/email';

const OrderSummary: FC = () => {
    const { handleBack, handleNext, setStep } = useContext(AppContext);

    const history = useHistory();
    const classes = useStyles();
    const globalStyles = varStyles();
    const dispatcher = useDispatch();

    const addAddress = useSelector((state: any) => state.addAddressReducer);
    const { addressResponse } = addAddress;

    const user: UserModel = useSelector((state: any) => state.authReducer.user);

    const cart = useSelector((state: any) => state.cart);
    const { cartItems } = cart;

    const order = useSelector((state: any) => state.order);
    const { loading, success, error: productError, offlineError } = order;

    let productsTotal = cartItems.reduce((accumulator: any, product: any) => product.cost * product.qty + accumulator, 0).toFixed(2);
    const deliveryCharge = 100;
    const orderTotal = Number(productsTotal) + Number(deliveryCharge);

    const [coupon, setCoupon] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Pago por entrega');
    const [deliveryDate, setDeliveryDate] = useState<Date | any>(Date.now() + 2 * 86400000); //Two days after
    const [deliveryTime, setDeliveryTime] = useState<Date | any>(Date.now() + 1000 * 60); //One minute after
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    const [deliveryErrors, setDeliveryErrors] = useState({
        date: '',
        time: ''
    });
    
    useEffect(() => {
        if (!navigator.onLine && loading) {
            dispatcher({ 
                type: ORDER_CONSTANTS.CREATE_ORDER_OFFLINE, 
                error: 'Sin conexión' 
            });
        }
    }, [navigator.onLine, loading]);

    useEffect(() => {
        dispatcher({ type: ORDER_CONSTANTS.CREATE_ORDER_RESET });
    }, []);
    
    useEffect(() => {
        dispatcher(getAddressAction(user.id));

        if (!user.id) {
            history.push('/login?redirect=checkoutprocess');
        }
    }, [history, dispatcher, user.id]);

    useEffect(() => {
        if (success) {
            for (let i = 0; i < cartItems.length; i++) {
                dispatcher(removeFromCart(cartItems[i].id));
            }
            dispatcher({ type: ORDER_CONSTANTS.CREATE_ORDER_RESET });
            sendOrderConfirmation(user.email, order.orderCreated, EmailTemplates.OrderConfirmation);
            handleNext();
        }

        if (cartItems.length === 0) {
            setStep(0);
        }
    }, [dispatcher, success, cartItems, history, user]);

    useEffect(() => {
        if (productError.length > 0) {
            for (let i = 0; i < productError.length; i++) {
                if (productError[i].status === 'updated') {
                    dispatcher(updateQty(productError[i].id, productError[i].new_qty));
                }
            }
        }
    }, [productError]);
    
    const onCloseScheduleModal = () => {
        setShowScheduleModal(false);
    }

    const handleCreateOrder = () => {
        let currentDate = Number(moment().startOf('day').format('X'));
        let currentTime = Number(moment().format('X'));

        const format = 'h:mm:ss';
        let openTime = moment('10:00:00', format);
        let closeTime = moment('18:00:00', format);
        let errors = { ...deliveryErrors };

        //Delivery Date validations
        if (!deliveryDate){
            errors = {...errors, date: 'Ingresa una fecha'};
        } else if (moment(dateToUnixTime(deliveryDate)).isBefore(currentDate)) {
            errors = {...errors, date: 'Fechas anteriores al día de hoy no válidas'};
        } else if (!/^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{4}$/.test(moment(deliveryDate).format('DD/MM/YYYY'))) {
            errors = {...errors, date: 'Formato de fecha incorrecta'};
        } else if (moment(deliveryDate).day() === 0 || moment(deliveryDate).day() === 6) { // Verificar sábado o domingo
            errors = {...errors, date: 'Día no disponible'};
            setShowScheduleModal(true);
        }

        //Delivery Time Validations
        if (!deliveryTime){
            errors = {...errors, time: 'Ingresa una hora válida'};
        } else if (moment(dateToUnixTime(deliveryTime)).isBefore(currentTime) && moment(dateToUnixTime(deliveryDate, 'day')).isSame(currentDate)) {
            errors = {...errors, time: 'La hora no puede ser menor o igual a la hora actual'};
        } else if (!/^(\d\d?|2[0-3]):?([0-5]\d)$/.test(moment(deliveryTime).format('LT'))) {
            errors = {...errors, time: 'Formato de hora inválido'};
        } else if (!moment(deliveryTime).isBetween(openTime, closeTime, 'minutes', '[]')) {
            errors = {...errors, time: 'Hora no disponible'};
            setShowScheduleModal(true);
        }

        setDeliveryErrors(errors);

        if (!errors.date && !errors.time) {
            dispatcher(createOrder({
                name: addressResponse.name, 
                phone: addressResponse.phone, 
                address: { 
                    cp: addressResponse.cp, 
                    city: addressResponse.city, 
                    district: addressResponse.district, 
                    street: addressResponse.street
                }, 
                email: addressResponse.email, 
                userRef: user.id,
                products: cartItems,
                deliverDate: deliveryDate,
                deliverTime: deliveryTime,
                deliveryCharge,
                productsTotal,
                orderTotal
            }));
        }
    }

    const checkProductStatus = (productId: string) => {
        for (let i = 0; i < productError?.length; i++) {
            if (productError[i].id === productId) {
                return true;
            }
        }
        return false;
    }

    const theme: any = createMuiTheme({
        palette: {
            primary: {
                main: '#000000'
            }
        }
    })

    return (
        <Container style={{ marginBottom: '2rem' }}>
            <ScreenLoader isOpen={loading} />
            <ModalLogout />
            <ScheduleNoticeModal isOpen={showScheduleModal} onClose={onCloseScheduleModal}/>
            <div className={classes.header}>
                <IconButton onClick={handleBack}>
                    <ArrowBack className='arrowBack' />
                </IconButton>
                <h1 className='title'>Resumen de pedido</h1>
            </div>
            <SnackBarAlert
                className={classes.snackBar}
                position={{ vertical: 'top', horizontal: 'center' }}
                open={productError.length > 0}
                message='Algunos productos cambiaron su estado'
                severity='error'
                hideDuration={null}
            />
            { offlineError && <SnackBarAlert message={offlineError} severity="error" open={!!offlineError} /> }
            <Grid
                container
                spacing={2}
            >
                <Grid item xs={12} sm={12} md={7} className={classes.addressGrid}>
                    <Paper className='addressPaper'>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={12}>
                                <Typography style={{ fontWeight: 'bold' }}  variant='h5'>Información de contacto</Typography>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <strong>Contacto</strong>
                            </Grid>
                            <Grid item xs={6} sm={8} style={{ wordBreak: 'break-word' }}>
                                {addressResponse?.email}
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <strong>Teléfono</strong>
                            </Grid>
                            <Grid item xs={6} sm={8}>
                                {addressResponse?.phone}
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <strong>Enviar a</strong>
                            </Grid>
                            <Grid item xs={6} sm={8}>
                                {addressResponse?.street}, {'Colonia'} {addressResponse?.district}, {'CP'} {addressResponse?.cp}, {addressResponse?.city}, {'Michoácan.'}
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <strong>Método</strong>
                            </Grid>
                            <Grid item xs={6} sm={8} style={{ display: 'flex', alignItems: 'center' }}>
                                <LocalAtm style={{ color: '#D32F2F', marginRight: '5px' }}/> <span>Efectivo</span>
                            </Grid>
                        </Grid>
                        <Grid xs={12} md={12} container item justify='flex-end'>
                            <Button color="secondary">CAMBIAR MÉTODO DE PAGO</Button>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={12} md={5}>
                    <Grid item sm={12} md={12} className={classes.productsContainer}>
                        {/* Desktop */}
                        <CartItems 
                            className={classes.cartItemsDesktop}
                            cartItems={cartItems} 
                            checkProductStatus={checkProductStatus} 
                            productError={productError} 
                        />
                        {/* Mobile */}
                        <Paper>
                            <Accordion square className={classes.cartItemsMobile}>
                                <AccordionSummary
                                    expandIcon={<ExpandMore />}
                                    aria-controls="cartItems-container"
                                    id="cartItems-container"
                                >
                                    <Typography style={{ fontWeight: 'bold', marginLeft: '15px' }} variant='h5'>Productos</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <CartItems 
                                        cartItems={cartItems} 
                                        checkProductStatus={checkProductStatus} 
                                        productError={productError} 
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </Paper>
                    </Grid>
                    {(productError.length > 0 && (cartItems.some((cartItem: any) => checkProductStatus(cartItem.id)))) && <Alert className={classes.alert} severity='error' >Algunos productos cambiaron su estado</Alert>}
                </Grid>
            </Grid>
            <Grid
                container
                spacing={2}
            >
                <Grid item xs={12} sm={12} md={7} className={classes.orderSummaryGrid}>
                    <Paper className='orderSummaryPaper'>
                        <Typography style={{ fontWeight: 'bold' }} variant='h5'>Resumen de pedido</Typography>
                        <Grid container spacing={2} style={{ margin: '20px 0px 20px 0px'}}>
                            <Grid xs={6} md={6} item justify='center'>
                                <strong>Productos </strong>
                            </Grid>
                            <Grid xs={6} md={6} container item justify='flex-end'>
                                MXN ${productsTotal}
                            </Grid>
                            <Grid xs={6} md={6} item>
                                <strong>Envío </strong>
                            </Grid>
                            <Grid xs={6} md={6} container item justify='flex-end'>
                                MXN ${deliveryCharge.toFixed(2)}
                            </Grid>
                            <Grid xs={6} md={6} item>
                                <strong className={globalStyles.primaryColor}>Total </strong>
                            </Grid>
                            <Grid xs={6} md={6} container item justify='flex-end'>
                                <strong className={classes.orderTotal}>MXN ${orderTotal.toFixed(2)}</strong>
                            </Grid>
                            <Grid xs={12} md={12} item>
                                <ThemeProvider theme={theme}>
                                    <FormControl fullWidth variant='outlined'>
                                        <InputLabel htmlFor="outlined-adornment-coupon">Ingresar cupón</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-coupon"
                                            type='text'
                                            value={coupon}
                                            onChange={(e) => setCoupon(e.target.value)}
                                            inputProps={{
                                                classes: {
                                                    notchedOutline: classes.notchedOutline
                                                }
                                            }}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <LocalActivityOutlined />
                                                </InputAdornment>
                                            }
                                            labelWidth={100}
                                        />
                                    </FormControl>
                                </ThemeProvider>
                            </Grid>
                            <Grid xs={12} md={12} container item justify='flex-end'>
                                <Button color="secondary">USAR CUPÓN</Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={12} md={5} className={classes.scheduleGrid}>
                    <Paper className='schedulePaper'>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography style={{ fontWeight: 'bold' }} variant='h5'>Fecha de envío</Typography>
                            <Tooltip 
                                title="Horario de Lunes a Viernes de 10:00 am a 6:00 pm" 
                                aria-label="Horario de Lunes a Viernes de 10:00 am a 6:00 pm"
                                placement="top"
                            >
                                <IconButton aria-label='schedule'>
                                    <Error style={{ color: '#D32F2F' }} />
                                </IconButton>
                            </Tooltip>
                        </div>
                        <ThemeProvider theme={theme}>
                            <Grid container>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <KeyboardDatePicker
                                        helperText={deliveryErrors.date}
                                        error={!!deliveryErrors.date}
                                        style={{  width: '100%'  }}
                                        disableToolbar
                                        variant="dialog"
                                        format="dd/MM/yyyy"
                                        margin="normal"
                                        id="estimated-date-arrival"
                                        label="Fecha de entrega"
                                        value={deliveryDate}
                                        minDate={Date.now()}
                                        onChange={(date) => setDeliveryDate(date)}
                                        onFocus={() => setDeliveryErrors(errors => ({...errors, date: ''}))}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        onClickCapture={() => setDeliveryErrors(errors => ({...errors, date: ''}))}
                                    />
                                    <KeyboardTimePicker
                                        helperText={deliveryErrors.time}
                                        error={!!deliveryErrors.time}
                                        style={{ width: '100%' }}
                                        margin="normal"
                                        id="estimated-time-arrival"
                                        label="Hora de entrega"
                                        value={deliveryTime}
                                        onChange={(date) => setDeliveryTime(date)}
                                        onFocus={() => setDeliveryErrors(errors => ({...errors, time: ''}))}
                                        KeyboardButtonProps={{
                                            'aria-label': 'change time',
                                        }}
                                        onClickCapture={() => setDeliveryErrors(errors => ({...errors, time: ''}))}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>
                        </ThemeProvider>
                    </Paper>
                    <Button
                        className={classes.button}
                        fullWidth
                        type='button'
                        variant='contained'
                        color='secondary'
                        onClick={handleCreateOrder}
                        disabled={cartItems.length === 0}
                    >
                        Confirmar
                    </Button>
                </Grid>
                <Button
                    className={classes.floatingButton}
                    fullWidth
                    type='button'
                    variant='contained'
                    color='secondary'
                    onClick={handleCreateOrder}
                    disabled={cartItems.length === 0}
                >
                    Confirmar
                </Button>
            </Grid>
        </Container>
    );
}

export default OrderSummary;